import { memo } from "react"

/**
 * Self-hosted Apple-style emoji PNGs (emoji-datasource-apple 64px).
 * Same art for every visitor — not the visitor's OS emoji font.
 */
const APPLE_EMOJI_BASE = "/emoji/apple"

/**
 * Map of emoji graphemes used in BitBoy moods → apple asset filename
 * (lowercase hex codepoints, matching emoji-datasource-apple).
 */
const APPLE_EMOJI_FILES: Readonly<Record<string, string>> = {
  "😴": "1f634.png",
  "😪": "1f62a.png",
  "💤": "1f4a4.png",
  "☕": "2615.png",
  "♨️": "2668-fe0f.png",
  "🌅": "1f305.png",
  "🌄": "1f304.png",
  "☀️": "2600-fe0f.png",
  "🍜": "1f35c.png",
  "🍲": "1f372.png",
  "🍛": "1f35b.png",
  "🎵": "1f3b5.png",
  "🎶": "1f3b6.png",
  "🎧": "1f3a7.png",
  "💻": "1f4bb.png",
  "⌨️": "2328-fe0f.png",
  "🖥️": "1f5a5-fe0f.png",
  "🌤️": "1f324-fe0f.png",
  "😎": "1f60e.png",
  "🌙": "1f319.png",
  "🌕": "1f315.png",
  "✨": "2728.png",
  "👋": "1f44b.png",
  "✋": "270b.png",
  "🤚": "1f91a.png",
  "💡": "1f4a1.png",
  "💭": "1f4ad.png",
  "🚀": "1f680.png",
  "📖": "1f4d6.png",
  "📚": "1f4da.png",
  "👀": "1f440.png",
  "🌧️": "1f327-fe0f.png",
  "💧": "1f4a7.png",
  "☔": "2614.png",
  "✅": "2705.png",
  "☑️": "2611-fe0f.png",
  "🎉": "1f389.png",
  "💛": "1f49b.png",
  "🧡": "1f9e1.png",
  "❤️": "2764-fe0f.png",
}

/**
 * Shared grapheme segmenter — Intl.Segmenter construction is expensive, so
 * one instance is reused across all calls (spec: instances are stateless
 * and safe to reuse).
 */
const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" })

/** True if this mood frame is entirely covered by our Apple emoji assets. */
function isAppleEmojiFrame(frame: string): boolean {
  if (!frame) return false
  // Segment into grapheme clusters (emoji + ZWJ + FE0F stay together).
  const chars = [...graphemeSegmenter.segment(frame)].map(
    (s) => s.segment
  )
  // Allow only emoji we host (no mixed ASCII+emoji frames for now).
  return chars.length > 0 && chars.every((c) => c in APPLE_EMOJI_FILES)
}

function appleEmojiSrc(emoji: string): string | null {
  const file = APPLE_EMOJI_FILES[emoji]
  return file ? `${APPLE_EMOJI_BASE}/${file}` : null
}

interface AppleEmojiProps {
  readonly emoji: string
  readonly className?: string
}

/** Single Apple-style emoji image. */
export const AppleEmoji = memo(function AppleEmoji({ emoji, className }: AppleEmojiProps) {
  const src = appleEmojiSrc(emoji)
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      className={className ?? "bitboy-thought-emoji"}
    />
  )
})

interface MoodFrameProps {
  readonly frame: string
}

/**
 * Renders a thought-bubble frame: Apple-style PNG(s) when the frame is an
 * emoji mood, plain monospaced text for ASCII moods.
 */
export const MoodFrame = memo(function MoodFrame({ frame }: MoodFrameProps) {
  if (!frame) return null

  if (isAppleEmojiFrame(frame)) {
    const graphemes = [...graphemeSegmenter.segment(frame)].map((s) => s.segment)
    return (
      <span className="bitboy-thought-emoji-row">
        {/* Frames are static strings rendered in fixed order — index keys are stable here. */}
        {graphemes.map((g, i) => (
          <AppleEmoji key={`${g}-${i}`} emoji={g} />
        ))}
      </span>
    )
  }

  return <span className="bitboy-thought-text">{frame}</span>
})
